import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle } from 'lucide-react';
import { StatTile, SectionHeading, EmptyState } from '../components/mailer/atoms';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mailer/dashboard`;

function IssueTile({ label, value, hint, testId }) {
  const bad = (value || 0) > 0;
  return (
    <div
      data-testid={testId}
      className={cn(
        'rounded-2xl border hairline px-5 py-4 transition flex items-start justify-between',
        bad ? 'bg-red-50/40 border-red-200' : 'bg-white dark:bg-neutral-900'
      )}
    >
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.2em] text-neutral-500 font-bold">{label}</div>
        <div className={cn('display text-[26px] font-bold tracking-tight mt-2 leading-none', bad ? 'text-red-700' : 'text-neutral-900 dark:text-neutral-100')}>
          {Number(value || 0).toLocaleString('en-US')}
        </div>
        {hint && <div className="text-[11px] text-neutral-500 mt-1.5">{hint}</div>}
      </div>
      <div className={cn('h-7 w-7 rounded-md flex items-center justify-center shrink-0', bad ? 'bg-red-100 text-red-700' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-400')}>
        {bad ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
      </div>
    </div>
  );
}

export default function MailerDataQuality() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    axios.get(`${API}/data-quality`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(reload, []);

  if (loading && !data) return <div className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto"><div className="h-72 rounded-2xl border hairline bg-white animate-pulse" /></div>;
  if (!data || data.total_mailed === 0) return <div className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto"><EmptyState /></div>;

  const tone = data.attribution_rate >= 80 ? 'bg-emerald-500' : data.attribution_rate >= 40 ? 'bg-amber-400' : 'bg-red-500';
  const toneText = data.attribution_rate >= 80 ? 'text-emerald-700' : data.attribution_rate >= 40 ? 'text-amber-700' : 'text-red-700';

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto space-y-6" data-testid="mailer-quality-page">
      <SectionHeading>Attribution Health</SectionHeading>
      <div className="rounded-2xl border hairline bg-white dark:bg-neutral-900 p-6">
        <SectionHeading>Engagement Attribution Rate</SectionHeading>
        <div className="flex items-end gap-4 mt-2">
          <div className={cn('display text-[64px] font-bold tracking-tight leading-none', toneText)}>{data.attribution_rate}%</div>
          <div className="text-[13px] text-neutral-500 mb-2">of mailed leads were claimed by an LO</div>
        </div>
        <div className="h-2 bg-neutral-100 dark:bg-neutral-800 rounded-full mt-4 overflow-hidden">
          <div className={cn('h-full transition-all duration-700', tone)} style={{ width: `${Math.max(2, data.attribution_rate)}%` }} data-testid="mailer-attrib-bar" />
        </div>
      </div>

      <div>
        <SectionHeading>Volume</SectionHeading>
        <div className="grid grid-cols-3 gap-3">
          <StatTile label="Mailed" value={data.total_mailed} testId="dq-mailed" />
          <StatTile label="Claimed" value={data.total_claimed} testId="dq-claimed" />
          <StatTile label="Closed" value={data.total_closed} testId="dq-closed" tone="positive" />
        </div>
      </div>

      <div>
        <SectionHeading>Issues</SectionHeading>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <IssueTile label="Missing Phone" value={data.missing_phone} testId="dq-no-phone" hint="cannot call back" />
          <IssueTile label="Missing Email" value={data.missing_email} testId="dq-no-email" hint="cannot email drip" />
          <IssueTile label="Missing State" value={data.missing_state} testId="dq-no-state" hint="excluded from States" />
          <IssueTile label="No Campaign Tag" value={data.missing_campaign} testId="dq-no-campaign" hint="will not roll into reports" />
          <IssueTile label="Duplicate Refs" value={data.duplicate_refs} testId="dq-dupes" hint="shouldn't happen — uploads dedupe" />
        </div>
      </div>

      {/* Helpful pointer toward the safer per-import revert flow now that
          the nuke-all button has been removed from this screen. */}
      <div className="rounded-2xl border hairline bg-neutral-50 dark:bg-neutral-900 p-5 text-[12.5px] text-neutral-600 dark:text-neutral-400 leading-relaxed">
        Need to undo a specific upload? Open <strong className="text-neutral-900 dark:text-neutral-200">Mailer → Upload → Recent imports</strong>, click an import to see its details, then use <strong className="text-neutral-900 dark:text-neutral-200">Revert this import</strong>. This deletes only the leads from that one batch — never your full pipeline.
      </div>

      {data.total_mailed > 0 && data.missing_phone === 0 && data.missing_state === 0 && data.duplicate_refs === 0 && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5 flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-700" />
          <div>
            <div className="text-[14px] font-bold text-emerald-900 display tracking-tight">Mailer data is clean</div>
            <div className="text-[12px] text-emerald-800">All required fields present, no duplicate references detected.</div>
          </div>
        </div>
      )}
    </div>
  );
}
